import { Box, Button, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import type { JSX } from "react";

import {
  BulkActionBar,
  BulkButton,
  BulkCheckbox,
  useBulkSelection,
} from "../../../../components/bulk-selection/index.js";
import { ConfirmDialog } from "../../../../components/ui/confirm-dialog.js";
import { PageLoader } from "@/components/ui/page-loader";
import { supportApi } from "../../client.js";
import { SupportErrorState } from "../../components/SupportEmptyState.js";
import type { SupportStatus } from "../../types.js";
import { STATUS_GRID } from "./grids.js";
import { NewStatusDialog } from "./NewStatusDialog.js";
import { StatusRow } from "./StatusRow.js";

/** Ticket statuses (the kanban columns) editor: list + inline edit + create
 * modal + single / bulk delete. Bulk delete fans out per-row admin DELETEs
 * (no `bulk_destroy` endpoint here yet). Extracted from `SettingsWorkflowsPanel`. */
export function StatusesEditor(): JSX.Element {
  const qc = useQueryClient();
  const statusesQuery = useQuery({
    queryKey: ["support", "statuses"],
    queryFn: supportApi.listStatuses,
  });

  const create = useMutation({
    mutationFn: (b: { name: string; category: string; color?: string }) =>
      supportApi.createStatus(b),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["support", "statuses"] }),
  });
  const update = useMutation({
    mutationFn: ({ id, body }: { id: number; body: Partial<SupportStatus> }) =>
      supportApi.updateStatus(id, body),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["support", "statuses"] }),
  });
  const remove = useMutation({
    mutationFn: (id: number) => supportApi.deleteStatus(id),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["support", "statuses"] }),
  });
  const bulkRemove = useMutation({
    // Fans out to the per-row admin DELETE in parallel. Settled-style
    // so a single 4xx doesn't strand the others mid-flight — the
    // surrounding invalidate refetches whatever actually stuck.
    mutationFn: async (ids: number[]) => {
      await Promise.allSettled(ids.map((id) => supportApi.deleteStatus(id)));
    },
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["support", "statuses"] }),
  });

  const rows = statusesQuery.data?.data ?? [];
  const bulk = useBulkSelection(rows);

  const [showCreate, setShowCreate] = useState(false);
  // Pending status delete (drives <ConfirmDialog>).
  const [pendingDelete, setPendingDelete] = useState<{ id: number; name: string } | null>(null);
  const [confirmBulk, setConfirmBulk] = useState(false);

  return (
    <Box component="section" sx={{ display: "grid", gap: 1.5 }}>
      <Box component="header" sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1.5 }}>
        <Box>
          <Typography component="h2" sx={{ fontSize: "1.125rem", fontWeight: 600, letterSpacing: "-0.01em" }}>
            Ticket statuses
          </Typography>
          <Typography sx={{ fontSize: 14, color: "text.secondary" }}>
            The kanban columns customers and agents see. Status colour
            propagates to badges across the app.
          </Typography>
        </Box>
        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon sx={{ fontSize: 16 }} />}
          onClick={() => setShowCreate(true)}
          data-testid="workflow-add-status"
        >
          Add status
        </Button>
      </Box>

      <BulkActionBar count={bulk.count} onClear={bulk.clear}>
        <BulkButton
          variant="destructive"
          disabled={bulkRemove.isPending}
          onClick={() => setConfirmBulk(true)}
        >
          <DeleteOutlineIcon sx={{ fontSize: 16 }} />
          Delete {bulk.count} {bulk.count === 1 ? "status" : "statuses"}
        </BulkButton>
      </BulkActionBar>

      {statusesQuery.isLoading && <PageLoader />}
      {statusesQuery.isError && (
        <SupportErrorState
          error={statusesQuery.error}
          resourceLabel="statuses"
        />
      )}

      {statusesQuery.data && (
        <Box component="ul" sx={{ display: "grid", gap: 1, listStyle: "none", m: 0, p: 0 }}>
          {rows.length > 0 && (
            <Box
              component="li"
              sx={{ display: "grid", gridTemplateColumns: STATUS_GRID, alignItems: "center", gap: 1.5, px: 1, fontSize: 12, color: "text.disabled" }}
            >
              <BulkCheckbox
                checked={bulk.allOnPageChecked}
                indeterminate={bulk.someOnPageChecked}
                onChange={(checked) => bulk.toggleAllOnPage(checked)}
                ariaLabel="Select all statuses"
              />
              <Box component="span" sx={{ gridColumn: "span 6" }}>Select all on page</Box>
            </Box>
          )}
          {rows.map((s) => (
            <StatusRow
              key={s.id}
              status={s}
              selected={bulk.isSelected(s.id)}
              onToggle={(checked, opts) => bulk.setChecked(s.id, checked, opts)}
              onSave={(body) => update.mutate({ id: s.id, body })}
              onDelete={() => setPendingDelete({ id: s.id, name: s.name })}
              isPending={update.isPending}
            />
          ))}
          {rows.length === 0 && (
            <Box component="li" sx={{ fontSize: 14, color: "text.disabled" }}>
              No statuses yet — use “Add status” above to create one.
            </Box>
          )}
        </Box>
      )}

      <NewStatusDialog
        isOpen={showCreate}
        onClose={() => setShowCreate(false)}
        onSubmit={(b) =>
          create.mutate(b, { onSuccess: () => setShowCreate(false) })
        }
        isSubmitting={create.isPending}
      />

      <ConfirmDialog
        isOpen={pendingDelete !== null}
        onClose={() => setPendingDelete(null)}
        title="Delete ticket status?"
        description={
          pendingDelete
            ? `"${pendingDelete.name}" will be deleted permanently. Tickets currently in this status will need to be moved.`
            : ""
        }
        confirmLabel="Delete status"
        onConfirm={() => {
          if (!pendingDelete) return;
          remove.mutate(pendingDelete.id);
        }}
      />

      <ConfirmDialog
        isOpen={confirmBulk}
        onClose={() => setConfirmBulk(false)}
        title={`Delete ${bulk.count} ${bulk.count === 1 ? "status" : "statuses"}?`}
        description="Tickets currently in any of these statuses will need to be moved. This cannot be undone."
        confirmLabel={`Delete ${bulk.count} ${bulk.count === 1 ? "status" : "statuses"}`}
        onConfirm={async () => {
          await bulkRemove.mutateAsync([...bulk.selected]);
          bulk.clear();
        }}
      />
    </Box>
  );
}
